/**
 * Verification Service — identity documents, driver licenses, and selfie verification.
 *
 * Handles:
 * - Image compression (1920px max, JPEG 0.75 quality)
 * - Supabase Storage upload to `verification` bucket
 * - identity_documents + driver_licenses table CRUD
 * - Overview computation from DB state
 *
 * Class-based singleton per CLAUDE.md architecture rules.
 */
import * as Crypto from 'expo-crypto'
import { supabase } from '../supabase'
import type { DbEnum, InsertRow, Json, UpdateRow } from '../types/db-rows'
import { ServiceError, verificationFailureClass } from '../errors/service-error'
import { compressAndUpload } from '../utils/image-compress'
import { validIsoDate } from '../utils/dates'
import { requireLiveUserId } from './session-identity'
import { verificationTransport, VerificationNetworkError } from './verification-transport'
import {
  QUALITAS_AML_DISCLAIMER_DOCUMENT_TYPE,
  QUALITAS_AML_DISCLAIMER_URL,
  QUALITAS_AML_DISCLAIMER_VERSION,
} from '../constants/aml'
import type {
  VerificationOverview,
  VerificationStep,
  VerificationStatus,
  DriverLicenseInput,
  SelfieInput,
  LiveSelfieSession,
  LiveSelfieDecision,
  LiveSelfieStatus,
  PhotoSubmitInput,
  PhotoSubmitResult,
  LicenseClass,
  BookingEligibility,
  BookingBlocker,
  ExtractedIdentityData,
  PendingIdentitySubmission,
  CanonicalVerificationStatus,
  VerificationProviderName,
} from '../types/verification.types'
import { BOOKING_BLOCKERS } from '../types/verification.types'

// ─── Constants ──────────────────────────────────────────────────────────────

const BUCKET = 'verification'

/**
 * The regular eligibility endpoint is deliberately profile-wide. A one-time
 * administrative deferral, however, is valid only for one vehicle and one
 * rental window. Keep that scope explicit at the client boundary so a cached
 * answer for car A can never be reused for car B.
 */
export interface BookingEligibilityScope {
  vehicleId?: string | null
  startAt?: string | null
  endAt?: string | null
}

type ScopedBookingEligibilityRow = {
  allowed: boolean
  missing: string[] | null
}

type ScopedBookingEligibilityRpcError = {
  message: string
  code?: string
  status?: number
  hint?: string | null
}

/**
 * `database.ts` is generated and is intentionally not hand-edited while a
 * migration is in flight. This narrow bridge is removed by the next generated
 * type refresh; it does not broaden the public Supabase client elsewhere.
 */
type ScopedBookingEligibilityRpcClient = {
  rpc: (
    functionName: 'booking_eligibility_for_draft',
    params: {
      p_vehicle_id: string
      p_start_at: string
      p_end_at: string
      p_requires_aml?: boolean
    },
  ) => {
    maybeSingle: () => Promise<{
      data: ScopedBookingEligibilityRow | null
      error: ScopedBookingEligibilityRpcError | null
    }>
  }
}

/**
 * Read the friendly server message out of a Supabase FunctionsHttpError.
 *
 * Supabase JS's `functions.invoke()` returns a `FunctionsHttpError` for any
 * non-2xx status with a generic message ("Edge Function returned a non 2xx
 * status code") — the actual server error body is buried in `.context`,
 * which is the underlying `Response`. We try to read it as JSON first
 * (our edge fns return `{ error: "…" }` shape via secureErrorResponse)
 * and fall back to text.
 */
async function readEdgeFnError(
  err: unknown,
): Promise<{ message: string | null; code: string | null }> {
  const ctx = (err as { context?: Response }).context
  if (!ctx || typeof ctx.clone !== 'function') return { message: null, code: null }
  try {
    const body = (await ctx.clone().json()) as {
      error?: unknown
      message?: unknown
      code?: unknown
    }
    const code = typeof body?.code === 'string' && body.code ? body.code : null
    if (typeof body?.error === 'string' && body.error) return { message: body.error, code }
    if (typeof body?.message === 'string' && body.message) return { message: body.message, code }
    return { message: null, code }
  } catch {
    try {
      const text = await ctx.clone().text()
      if (text) return { message: text.slice(0, 200), code: null }
    } catch {
      // body unreadable — null falls back to the generic message
    }
  }
  return { message: null, code: null }
}

async function readEdgeFnErrorBody(err: unknown): Promise<string | null> {
  return (await readEdgeFnError(err)).message
}

/** El status HTTP real del fallo, o null si el error no trae Response. */
function httpStatusOf(err: unknown): number | null {
  const context = (err as { context?: Response }).context
  return typeof context?.status === 'number' ? context.status : null
}

/**
 * Stable identifiers first, prose second.
 *
 * The prose matching below is how this drifted: the guard looks for "no pudimos completar la
 * validación" and the 500 says "…la verificación", one word apart, so the retry-able failure
 * rendered as the generic dead end. Rewording a server message must never change what the app
 * says, so a code — when the endpoint sends one — wins outright.
 */
const VERIFICATION_ERROR_KEYS: Record<string, string> = {
  VERIFICATION_UNAVAILABLE: 'verification.verifik_unavailable',
  IDENTITY_VERIFICATION_IN_PROGRESS: 'verification.identity_processing_notice',
  SELFIE_RATE_LIMITED: 'verification.selfie_rate_limited',
  VERIFICATION_RATE_LIMITED: 'verification.rate_limited',
}

function verificationUserMessageKey(message: string | null, code?: string | null): string {
  if (code && VERIFICATION_ERROR_KEYS[code]) return VERIFICATION_ERROR_KEYS[code]
  const normalized = message?.toLowerCase() ?? ''
  if (normalized.includes('sertracen') || normalized.includes('official license')) {
    return 'verification.sertracen_unavailable'
  }
  if (normalized.includes('no pudo leer')) {
    return 'verification.verifik_read_failed'
  }
  if (normalized.includes('no encontramos')) {
    return 'verification.verifik_not_found_generic'
  }
  if (normalized.includes('nombres')) {
    return 'verification.verifik_name_mismatch'
  }
  if (normalized.includes('sigue consultando') || normalized.includes('validación del dui tardó')) {
    return 'verification.verifik_timeout_lookup'
  }
  if (normalized.includes('tardó demasiado') || normalized.includes('tardó más de lo esperado')) {
    return 'verification.verifik_timeout_reading'
  }
  if (
    normalized.includes('no pudimos validar') ||
    normalized.includes('no pudimos completar la validación')
  ) {
    return 'verification.verifik_unavailable'
  }
  if (normalized.includes('no selfie uploaded') || normalized.includes('selfie upload')) {
    return 'verification.selfie_upload_failed'
  }
  if (normalized.includes('liveness')) {
    return 'verification.selfie_liveness_failed'
  }
  if (normalized.includes('face') || normalized.includes('match')) {
    return 'verification.selfie_match_failed'
  }
  return 'verification.error_failed'
}

/**
 * Map the `canonical_status` enum from the verification provider
 * abstraction layer (migration 20260411000001) onto the legacy
 * `verification_status` enum the rest of the app reads. Edge fns
 * dual-write both columns, so during the rollover window we PREFER
 * canonical (it's the post-decision source of truth) and fall back to
 * legacy for rows inserted before the dual-write went live.
 *
 *   canonical 'pending'      → legacy 'pending'
 *   canonical 'in_progress'  → legacy 'submitted'   (provider working on it)
 *   canonical 'review'       → legacy 'submitted'   (manual review queue)
 *   canonical 'approved'     → legacy 'approved'
 *   canonical 'declined'     → legacy 'rejected'
 *   canonical 'expired'      → legacy 'expired'
 */
function canonicalToLegacy(canonical: string): VerificationStatus {
  switch (canonical) {
    case 'approved':
      return 'approved'
    case 'declined':
      return 'rejected'
    case 'in_progress':
    case 'review':
      return 'submitted'
    case 'expired':
      return 'expired'
    case 'pending':
    default:
      return 'pending'
  }
}

function extractStatus(row: Record<string, unknown> | null): VerificationStatus {
  if (!row) return 'pending'
  const canonical = row.canonical_status as string | undefined | null
  if (canonical) return canonicalToLegacy(canonical)
  return (row.status as VerificationStatus) ?? 'pending'
}

function extractRejection(row: Record<string, unknown> | null): string | null {
  if (!row) return null
  return (row.rejection_reason as string) ?? null
}

function liveSelfieToLegacy(status: string | null | undefined): VerificationStatus {
  switch (status) {
    case 'approved':
      return 'approved'
    case 'declined':
    case 'abandoned':
      return 'rejected'
    case 'expired':
      return 'expired'
    case 'created':
    case 'not_started':
      return 'pending'
    case 'in_progress':
    case 'review':
    case 'error':
    default:
      return 'submitted'
  }
}

function hasVerifiedAt(row: Record<string, unknown> | null): boolean {
  return typeof row?.verified_at === 'string' && row.verified_at.length > 0
}

function isApprovedVerificationRow(row: Record<string, unknown> | null): boolean {
  if (!row) return false
  const status = extractStatus(row)
  return status === 'approved' || (status !== 'expired' && hasVerifiedAt(row))
}

function pickBestVerificationRow(rows: Record<string, unknown>[]): Record<string, unknown> | null {
  return rows.find(isApprovedVerificationRow) ?? rows[0] ?? null
}

function pickBestLiveSelfieRow(rows: Record<string, unknown>[]): Record<string, unknown> | null {
  return (
    rows.find((row) => liveSelfieToLegacy(row.status as string | null) === 'approved') ??
    rows.find(hasVerifiedAt) ??
    rows[0] ??
    null
  )
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return null
}

function asLiveSelfieStatus(value: unknown): LiveSelfieStatus | null {
  if (typeof value !== 'string') return null
  const allowed: readonly LiveSelfieStatus[] = [
    'created',
    'not_started',
    'in_progress',
    'approved',
    'declined',
    'review',
    'abandoned',
    'expired',
    'error',
  ]
  return allowed.includes(value as LiveSelfieStatus) ? (value as LiveSelfieStatus) : null
}

function asLiveSelfieSession(value: unknown): LiveSelfieSession | null {
  const record = asRecord(value)
  const status = asLiveSelfieStatus(record?.status)
  if (!record || !status) return null
  const sessionId = record.sessionId
  const providerSessionId = record.providerSessionId
  const sessionToken = record.sessionToken
  const url = record.url
  const expiresAt = record.expiresAt
  if (typeof sessionId !== 'string' || typeof providerSessionId !== 'string') return null
  return {
    sessionId,
    providerSessionId,
    sessionToken: typeof sessionToken === 'string' ? sessionToken : null,
    status,
    url: typeof url === 'string' ? url : null,
    expiresAt: typeof expiresAt === 'string' ? expiresAt : null,
  }
}

function asLiveSelfieDecision(value: unknown): LiveSelfieDecision | null {
  const record = asRecord(value)
  const status = asLiveSelfieStatus(record?.status)
  if (!record || !status) return null
  const sessionId = record.sessionId
  const providerSessionId = record.providerSessionId
  if (typeof sessionId !== 'string' || typeof providerSessionId !== 'string') return null
  return {
    sessionId,
    providerSessionId,
    status,
    livenessScore: typeof record.livenessScore === 'number' ? record.livenessScore : null,
    faceMatchScore: typeof record.faceMatchScore === 'number' ? record.faceMatchScore : null,
    rejectionReason: typeof record.rejectionReason === 'string' ? record.rejectionReason : null,
  }
}

// ─── Service ────────────────────────────────────────────────────────────────

type IdentityDocumentInsert = InsertRow<'identity_documents'>
type IdentityDocumentUpdate = UpdateRow<'identity_documents'>
type DriverLicenseInsert = InsertRow<'driver_licenses'>
type DriverLicenseUpdate = UpdateRow<'driver_licenses'>

/**
 * Statuses considered "in-flight" — a profile may have at most ONE row in
 * any of these statuses per (profile_id, document_type) for identity_documents
 * and per profile_id for driver_licenses. Backed server-side by the partial
 * UNIQUE indexes added in migration 20260501100000_identity_upsert_constraints.
 *
 * If a row in one of these states already exists when the user re-submits,
 * we UPDATE it in place instead of inserting a duplicate (which is what the
 * old `.upsert(..., { onConflict: 'profile_id' })` was trying — and failing — to do).
 */
const ACTIVE_VERIFICATION_STATUSES = ['pending', 'submitted'] as const

type IdDocumentTypeForPending = 'dui' | 'passport' | 'license' | 'cedula' | 'nie'

const CANONICAL_STATUSES: readonly CanonicalVerificationStatus[] = [
  'pending',
  'in_progress',
  'approved',
  'declined',
  'review',
  'expired',
]

function asCanonicalStatus(value: unknown): CanonicalVerificationStatus | null {
  if (typeof value !== 'string') return null
  return CANONICAL_STATUSES.includes(value as CanonicalVerificationStatus)
    ? (value as CanonicalVerificationStatus)
    : null
}

/** Shape of the verify-identity / verify-license edge response payload. */
interface IdentityVerifyEdgeResponse {
  nextStep?: 'selfie'
  status?: CanonicalVerificationStatus
  provider?: VerificationProviderName
  extracted?: ExtractedIdentityData
  confidence?: number
  rejectionReason?: string | null
  nameMatched?: boolean
}

/** Shape of the verify-selfie edge response payload. */
interface SelfieVerifyEdgeResponse {
  status?: 'approved' | 'declined' | 'review'
  livenessScore?: number | null
  faceMatchScore?: number | null
  rejectionReason?: string | null
}

/**
 * The extracted fields as the client should present them, joined from the
 * structured `extracted_data` blob AND the canonical columns. The columns
 * win the number: `document_number` is the value the provider verified.
 */
function extractedFromRow(row: Record<string, unknown>): ExtractedIdentityData {
  const blob = asRecord(row.extracted_data) ?? {}
  const joinedName =
    typeof row.first_name === 'string' && typeof row.last_name === 'string'
      ? `${row.first_name} ${row.last_name}`.trim()
      : ''
  return {
    firstName:
      (typeof blob.firstName === 'string' && blob.firstName) ||
      (typeof row.first_name === 'string' ? row.first_name : null) ||
      null,
    lastName:
      (typeof blob.lastName === 'string' && blob.lastName) ||
      (typeof row.last_name === 'string' ? row.last_name : null) ||
      null,
    fullName: (typeof blob.fullName === 'string' && blob.fullName) || joinedName || null,
    dateOfBirth:
      (typeof blob.dateOfBirth === 'string' && blob.dateOfBirth) ||
      (typeof row.date_of_birth === 'string' ? row.date_of_birth : null) ||
      null,
    documentNumber:
      (typeof blob.documentNumber === 'string' && blob.documentNumber) ||
      (typeof row.document_number === 'string' ? row.document_number : null) ||
      null,
    issuingCountry: typeof blob.issuingCountry === 'string' ? blob.issuingCountry : null,
    warnings: Array.isArray(blob.warnings)
      ? blob.warnings.filter((w): w is string => typeof w === 'string')
      : undefined,
  }
}

function hasReadableIdentityExtraction(extracted: ExtractedIdentityData): boolean {
  return Boolean(
    extracted.firstName ||
    extracted.lastName ||
    extracted.fullName ||
    extracted.dateOfBirth ||
    extracted.documentNumber,
  )
}

/**
 * La respuesta se perdió (timeout/red), no fue rechazada. El servidor sigue
 * procesando: se lee la fila de vuelta. Si hay decisión o extracción, se
 * devuelve y la persona NUNCA recaptura. Si nada aterrizó, null — y el
 * llamador responde con la acción siguiente honesta.
 */
async function reconcileIdentityRow(
  profileId: string,
  recordId: string,
): Promise<PhotoSubmitResult | null> {
  const { data, error } = await supabase
    .from('identity_documents')
    .select(
      'id, status, canonical_status, provider_status, verification_provider, extracted_data, ' +
        'confidence, rejection_reason, document_number, first_name, last_name, date_of_birth',
    )
    .eq('id', recordId)
    .eq('profile_id', profileId)
    .maybeSingle()

  if (error) throw ServiceError.fromSupabaseError(error)
  const row = data as Record<string, unknown> | null
  if (!row) return null

  const canonical = asCanonicalStatus(row.canonical_status)
  const legacy = extractStatus(row)
  const extracted = extractedFromRow(row)
  const decision = canonical === 'approved' || canonical === 'declined' || canonical === 'expired'
  const provider =
    typeof row.verification_provider === 'string' &&
    ['verifik', 'didit', 'claude-vision', 'document-ai', 'sertracen', 'manual'].includes(
      row.verification_provider,
    )
      ? (row.verification_provider as VerificationProviderName)
      : 'manual'

  if (!decision && !hasReadableIdentityExtraction(extracted)) return null

  return {
    recordId,
    identityDocumentId: recordId,
    status:
      canonical ??
      (legacy === 'approved' ? 'approved' : legacy === 'rejected' ? 'declined' : 'pending'),
    provider,
    extracted,
    confidence: typeof row.confidence === 'number' ? row.confidence : undefined,
    rejectionReason: (row.rejection_reason as string) ?? null,
    reconciled: true,
    nextStep: row.provider_status === 'active_unverified' ? 'selfie' : undefined,
  }
}

/**
 * Once the photos and row exist, an Edge Function failure must never send the
 * person back to the camera. First read the durable row in case the response
 * was merely lost; otherwise return a retryable error carrying the persisted
 * id so the UI can resume against exactly the same evidence.
 */
async function recoverPersistedIdentityFailure(
  profileId: string,
  recordId: string,
  verifyError: unknown,
): Promise<PhotoSubmitResult> {
  try {
    const reconciled = await reconcileIdentityRow(profileId, recordId)
    if (reconciled) return reconciled
  } catch {
    // The original failure is still the useful cause. A transient read error
    // must not erase the fact that the row id and photos were persisted.
  }

  if (verifyError instanceof VerificationNetworkError) {
    throw new ServiceError({
      message: verifyError.message,
      code: 'VERIFICATION_INCOMPLETE',
      statusCode: 503,
      details: { persistedRecordId: recordId },
      userMessageKey: 'verification.timeout_saved_notice',
      cause: verifyError,
    })
  }

  const { message: serverMessage, code: upstreamCode } = await readEdgeFnError(verifyError)
  const response = (verifyError as { context?: Response }).context
  const statusCode = typeof response?.status === 'number' ? response.status : 503

  // Authentication/ownership failures need a fresh session or support, not a
  // retry loop. Every other failure remains resumable because the evidence is
  // already owner-scoped and safely stored.
  if (statusCode === 401 || statusCode === 403) {
    throw new ServiceError({
      message:
        serverMessage ??
        (verifyError instanceof Error ? verifyError.message : 'Identity verification failed'),
      code: 'VERIFICATION_FAILED',
      statusCode,
      details: { persistedRecordId: recordId, upstreamCode },
      userMessageKey: verificationUserMessageKey(serverMessage, upstreamCode),
      cause: verifyError,
    })
  }

  throw new ServiceError({
    message:
      serverMessage ??
      (verifyError instanceof Error ? verifyError.message : 'Identity verification incomplete'),
    code: 'VERIFICATION_INCOMPLETE',
    statusCode,
    details: { persistedRecordId: recordId, upstreamCode },
    userMessageKey:
      upstreamCode === 'VERIFICATION_RATE_LIMITED' ||
      upstreamCode === 'IDENTITY_VERIFICATION_IN_PROGRESS'
        ? verificationUserMessageKey(serverMessage, upstreamCode)
        : 'verification.timeout_saved_notice',
    cause: verifyError,
  })
}

function identityWriteFailure(error: Parameters<typeof ServiceError.fromSupabaseError>[0]) {
  return error.code === '55P03'
    ? new ServiceError({
        message: error.message,
        code: 'IDENTITY_VERIFICATION_IN_PROGRESS',
        statusCode: 409,
        userMessageKey: 'verification.identity_processing_notice',
      })
    : ServiceError.fromSupabaseError(error)
}

class VerificationServiceImpl {
  /**
   * Find the current in-flight identity document for (profile, type), if any.
   * Returns the row id so we know whether to UPDATE or INSERT.
   */
  private async findActiveIdentityDocument(
    userId: string,
    documentType: DbEnum<'id_document_type'>,
  ): Promise<{ id: string } | null> {
    const { data, error } = await supabase
      .from('identity_documents')
      .select('id')
      .eq('profile_id', userId)
      .eq('document_type', documentType)
      // Spread the readonly tuple into the mutable enum array `.in()` wants;
      // the element type stays `verification_status`, so no cast is needed.
      .in('status', [...ACTIVE_VERIFICATION_STATUSES])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (error) throw ServiceError.fromSupabaseError(error)
    return data ?? null
  }

  /**
   * INSERT a new identity_documents row OR UPDATE the existing in-flight one.
   * Replaces the broken `.upsert(..., { onConflict: 'profile_id' })` pattern
   * — see migration 20260501100000_identity_upsert_constraints.sql for the
   * full background.
   *
   * Defense in depth: even with the partial UNIQUE migration
   * 20260501160000_identity_partial_unique.sql in place, an INSERT can
   * still collide with a leftover row from before that migration ran (or
   * a parallel submission winning the race). On 23505 we fall through
   * to UPDATE the most-recent row of any status for this (profile, type),
   * which is what the user effectively wanted anyway — they're trying to
   * (re)submit a document of this type, not create a parallel history.
   */
  private async upsertActiveIdentityDocument(
    userId: string,
    documentType: DbEnum<'id_document_type'>,
    payload: Omit<IdentityDocumentInsert, 'profile_id' | 'document_type'>,
  ): Promise<{ id: string }> {
    const existing = await this.findActiveIdentityDocument(userId, documentType)
    if (existing?.id) {
      const updatePayload: IdentityDocumentUpdate = { ...payload }
      const { data, error } = await supabase
        .from('identity_documents')
        .update(updatePayload)
        .eq('id', existing.id)
        .select('id')
        .single()
      if (error) throw identityWriteFailure(error)
      return data
    }
    const insertPayload: IdentityDocumentInsert = {
      profile_id: userId,
      document_type: documentType,
      ...payload,
    }
    const { data, error } = await supabase
      .from('identity_documents')
      .insert(insertPayload)
      .select('id')
      .single()
    if (!error) return data

    // 23505 = unique_violation. An older rejected/expired row of the same
    // (profile, type, doc_number) is occupying the unique slot. Find the
    // most-recent row of this type and reuse it instead of dead-ending.
    if ((error as { code?: string }).code === '23505') {
      const reuse = await supabase
        .from('identity_documents')
        .select('id')
        .eq('profile_id', userId)
        .eq('document_type', documentType)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (reuse.data?.id) {
        const { data: updated, error: updateErr } = await supabase
          .from('identity_documents')
          .update(payload as IdentityDocumentUpdate)
          .eq('id', reuse.data.id)
          .select('id')
          .single()
        if (updateErr) throw identityWriteFailure(updateErr)
        return updated
      }
    }
    throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Find the current in-flight driver license for the profile, if any.
   * driver_licenses has one active row per profile (no per-type split).
   */
  private async findActiveDriverLicense(userId: string): Promise<{ id: string } | null> {
    const { data, error } = await supabase
      .from('driver_licenses')
      .select('id')
      .eq('profile_id', userId)
      // Spread the readonly tuple into the mutable enum array `.in()` wants;
      // the element type stays `verification_status`, so no cast is needed.
      .in('status', [...ACTIVE_VERIFICATION_STATUSES])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (error) throw ServiceError.fromSupabaseError(error)
    return data ?? null
  }

  /**
   * INSERT a new driver_licenses row OR UPDATE the existing in-flight one.
   * Mirror of upsertActiveIdentityDocument for the licenses table —
   * including the 23505 fallback to UPDATE-the-most-recent-row when an
   * old placeholder occupies the (profile, country, license_number) slot.
   */
  private async upsertActiveDriverLicense(
    userId: string,
    payload: Omit<DriverLicenseInsert, 'profile_id'>,
  ): Promise<{ id: string }> {
    const existing = await this.findActiveDriverLicense(userId)
    if (existing?.id) {
      const updatePayload: DriverLicenseUpdate = { ...payload }
      const { data, error } = await supabase
        .from('driver_licenses')
        .update(updatePayload)
        .eq('id', existing.id)
        .select('id')
        .single()
      if (error) throw ServiceError.fromSupabaseError(error)
      return data
    }
    const insertPayload: DriverLicenseInsert = {
      profile_id: userId,
      ...payload,
    }
    const { data, error } = await supabase
      .from('driver_licenses')
      .insert(insertPayload)
      .select('id')
      .single()
    if (!error) return data

    if ((error as { code?: string }).code === '23505') {
      const reuse = await supabase
        .from('driver_licenses')
        .select('id')
        .eq('profile_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (reuse.data?.id) {
        const { data: updated, error: updateErr } = await supabase
          .from('driver_licenses')
          .update(payload as DriverLicenseUpdate)
          .eq('id', reuse.data.id)
          .select('id')
          .single()
        if (updateErr) throw ServiceError.fromSupabaseError(updateErr)
        return updated
      }
    }
    throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Get full verification overview for a user.
   * Queries identity_documents + driver_licenses and computes step statuses.
   */
  /**
   * Record acceptance of the insurer's declaration as its own act.
   *
   * Every verified user signs this, questionnaire or not, so it cannot live on
   * `aml_declarations` — that row only exists once someone starts the
   * questionnaire. `legal_acceptances` is append-only and versioned, which is
   * what makes it usable as proof: a row is a person, a wording, and a moment.
   *
   * Idempotent by re-insert rather than by upsert. A second acceptance of the
   * same version is a second signature of the same text, and overwriting the
   * first would destroy the earlier timestamp — the one thing the record
   * exists to preserve.
   */
  async acceptQualitasDisclaimer(userId: string): Promise<void> {
    const { data: existing, error: readError } = await supabase
      .from('legal_acceptances')
      .select('id')
      .eq('profile_id', userId)
      .eq('document_type', QUALITAS_AML_DISCLAIMER_DOCUMENT_TYPE)
      .eq('document_version', QUALITAS_AML_DISCLAIMER_VERSION)
      .limit(1)
    if (readError) throw ServiceError.fromSupabaseError(readError)
    if ((existing ?? []).length > 0) return

    const { error } = await supabase.from('legal_acceptances').insert({
      profile_id: userId,
      document_type: QUALITAS_AML_DISCLAIMER_DOCUMENT_TYPE,
      document_version: QUALITAS_AML_DISCLAIMER_VERSION,
      document_url: QUALITAS_AML_DISCLAIMER_URL,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
  }

  async getOverview(userId: string): Promise<VerificationOverview> {
    const [identityResult, licenseResult, selfieResult, disclaimerResult] = await Promise.all([
      supabase
        .from('identity_documents')
        .select(
          'status, canonical_status, rejection_reason, verified_at, selfie_image_path, liveness_score, face_match_score',
        )
        .eq('profile_id', userId)
        .order('created_at', { ascending: false })
        .limit(20),
      supabase
        .from('driver_licenses')
        .select('status, canonical_status, rejection_reason, verified_at')
        .eq('profile_id', userId)
        .order('created_at', { ascending: false })
        .limit(20),
      supabase
        .from('selfie_verifications')
        .select('status, rejection_reason, verified_at, liveness_score, face_match_score')
        .eq('profile_id', userId)
        .order('created_at', { ascending: false })
        .limit(20),
      // Read from legal_acceptances rather than aml_declarations.disclaimer_accepted_at:
      // the declaration row only exists once someone starts the questionnaire, and
      // the declaration is now required of every verified user, questionnaire or not.
      // legal_acceptances is also the immutable record, versioned, with IP and
      // user agent — which is what Art. 54 D.L. 144/2024 asks Tago to be able to show.
      supabase
        .from('legal_acceptances')
        .select('document_version, accepted_at')
        .eq('profile_id', userId)
        .eq('document_type', QUALITAS_AML_DISCLAIMER_DOCUMENT_TYPE)
        .eq('document_version', QUALITAS_AML_DISCLAIMER_VERSION)
        .order('accepted_at', { ascending: false })
        .limit(1),
    ])

    if (identityResult.error) throw ServiceError.fromSupabaseError(identityResult.error)
    if (licenseResult.error) throw ServiceError.fromSupabaseError(licenseResult.error)
    if (selfieResult.error) throw ServiceError.fromSupabaseError(selfieResult.error)
    if (disclaimerResult.error) throw ServiceError.fromSupabaseError(disclaimerResult.error)

    const identityRows = (identityResult.data ?? []) as Record<string, unknown>[]
    const licenseRows = (licenseResult.data ?? []) as Record<string, unknown>[]
    const selfieRows = (selfieResult.data ?? []) as Record<string, unknown>[]
    const identityRow = pickBestVerificationRow(identityRows)
    const licenseRow = pickBestVerificationRow(licenseRows)
    const selfieRow = pickBestLiveSelfieRow(selfieRows)

    const identityStatus: VerificationStatus = isApprovedVerificationRow(identityRow)
      ? 'approved'
      : extractStatus(identityRow)

    const legacyHasSelfie = !!identityRow?.selfie_image_path
    const legacyHasSelfieDecision = identityRow
      ? (identityRow.liveness_score !== null && identityRow.liveness_score !== undefined) ||
        (identityRow.face_match_score !== null && identityRow.face_match_score !== undefined)
      : false
    const selfieStatus: VerificationStatus = selfieRow
      ? hasVerifiedAt(selfieRow) &&
        liveSelfieToLegacy(selfieRow.status as string | null) !== 'expired'
        ? 'approved'
        : liveSelfieToLegacy(selfieRow.status as string | null)
      : !legacyHasSelfie
        ? 'pending'
        : legacyHasSelfieDecision
          ? extractStatus(identityRow)
          : 'submitted'

    const identity: VerificationStep = {
      type: 'identity',
      status: identityStatus,
      rejectionReason: identityStatus === 'approved' ? null : extractRejection(identityRow),
    }

    const license: VerificationStep = {
      type: 'license',
      status: isApprovedVerificationRow(licenseRow) ? 'approved' : extractStatus(licenseRow),
      rejectionReason: isApprovedVerificationRow(licenseRow) ? null : extractRejection(licenseRow),
    }

    const selfie: VerificationStep = {
      type: 'selfie',
      status: selfieStatus,
      rejectionReason: selfieRow
        ? extractRejection(selfieRow)
        : legacyHasSelfie
          ? extractRejection(identityRow)
          : null,
    }

    // The declaration is a signature, not a review: there is no submitted or
    // rejected state for it. Either the person accepted the current version or
    // they have not. A version bump reopens the step, which is the point —
    // consent proves nothing about wording the person never saw.
    const disclaimer: VerificationStep = {
      type: 'disclaimer',
      status: (disclaimerResult.data ?? []).length > 0 ? 'approved' : 'pending',
      rejectionReason: null,
    }

    // Guest KYC = same three facts as `has_complete_verification`.
    // Host publish = DUI + selfie only (`has_host_identity_for_listing`);
    // a host does not need a driver license to list cars.
    // Qualitas declaration lives on the AML screen, not on these flags.
    const isFullyVerified =
      identity.status === 'approved' &&
      license.status === 'approved' &&
      selfie.status === 'approved'
    const isHostPublishReady = identity.status === 'approved' && selfie.status === 'approved'

    return { identity, license, selfie, disclaimer, isFullyVerified, isHostPublishReady }
  }

  /**
   * Verificación contra el registro oficial del país que emitió el documento.
   *
   * El número y la fecha que escribió la persona van a la consulta oficial, y
   * las fotos del frente y el reverso son evidencia obligatoria: se guardan en
   * la misma fila de `identity_documents` ANTES de llamar al proveedor, así que
   * una aprobación siempre tiene un documento detrás que se puede mirar.
   *
   * Se llamaba `submitOfficialDuiLookup` y escribía `'SV'` a mano. Ese literal
   * era toda la restricción geográfica del producto.
   */
  async submitDriverLicense(userId: string, input: DriverLicenseInput): Promise<void> {
    // Same invariant: storage path folder and the driver_licenses row
    // (profile_id = auth.uid()) must come from the LIVE session identity, not a
    // cached user id that may have drifted.
    const liveId = await requireLiveUserId()
    void userId

    const frontPath = `${liveId}/license/${Crypto.randomUUID()}.jpg`
    const backPath = `${liveId}/license/${Crypto.randomUUID()}.jpg`

    const [frontStoragePath, backStoragePath] = await Promise.all([
      compressAndUpload(input.frontImageUri, BUCKET, frontPath),
      compressAndUpload(input.backImageUri, BUCKET, backPath),
    ])

    const issueDate = validIsoDate(input.issueDate)
    const expiryDate = validIsoDate(input.expiryDate)
    await this.upsertActiveDriverLicense(liveId, {
      license_number: input.licenseNumber,
      license_class: [input.licenseClass],
      issue_date: issueDate ?? new Date().toISOString().slice(0, 10),
      expiry_date:
        expiryDate ?? new Date(Date.now() + 365 * 24 * 3600_000).toISOString().slice(0, 10),
      front_image_path: frontStoragePath,
      back_image_path: backStoragePath,
      issuing_country: 'SV',
      status: 'submitted',
      canonical_status: 'review',
      rejection_reason: null,
      verified_at: null,
      verified_by: null,
    })
  }

  /**
   * update the license_class on the user's active
   * driver_licenses row from the photo-first confirmation step.
   *
   * Background: `submitAndVerifyLicense` seeds `license_class: ['liviana']`
   * during the initial upsert because the schema column is NOT NULL and
   * we need a value before OCR runs. The `verify-license` edge fn then
   * extracts documentNumber / expiryDate / issueDate but DOES NOT extract
   * the class (small text, varies by country, low OCR confidence) — so
   * the 'liviana' default would silently persist forever.
   *
   * The license.tsx photo-first screen now shows a Select for the class
   * after OCR returns and calls this on Confirm so the user always has
   * the chance to correct it. Default 'liviana' is correct for ~90% of
   * SV drivers but the explicit confirmation step prevents the silent-
   * wrong-class case.
   */
  async updateLicenseClass(profileId: string, licenseClass: LicenseClass): Promise<void> {
    const { error } = await supabase
      .from('driver_licenses')
      .update({ license_class: [licenseClass] })
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false })
      .limit(1)
    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Final Salvadoran driver-license decision. The photo-first step extracts
   * license fields, but production approval must come from Sertracen's public
   * control-number consultation through our server-side Render verifier.
   */
  async verifyDriverLicenseWithSertracen(
    profileId: string,
    input: {
      driverLicenseId: string
      licenseNumber?: string | null
      duiNumber: string
      controlPrefix: string
      controlNumber: string
      licenseClass: LicenseClass
      issueDate?: string | null
      expiryDate?: string | null
    },
  ): Promise<PhotoSubmitResult> {
    if (!profileId) {
      throw ServiceError.unauthorized('Authentication required for license verification')
    }

    await this.updateConfirmedDriverLicenseFields(profileId, {
      driverLicenseId: input.driverLicenseId,
      licenseNumber: input.licenseNumber,
      licenseClass: input.licenseClass,
      issueDate: input.issueDate,
      expiryDate: input.expiryDate,
    })

    const { data: verifyData, error: verifyError } =
      await verificationTransport.invoke<IdentityVerifyEdgeResponse>(
        'verify-license',
        {
          body: {
            driverLicenseId: input.driverLicenseId,
            duiNumber: input.duiNumber,
            // Backward-compatible field name for already-warm Edge Function
            // isolates and for the Render verifier contract.
            licenseNumber: input.duiNumber,
            controlPrefix: input.controlPrefix,
            controlNumber: input.controlNumber,
          },
        },
        { recordId: input.driverLicenseId },
      )

    if (verifyError) {
      if (verifyError instanceof VerificationNetworkError) {
        throw new ServiceError({
          message: verifyError.message,
          code: 'VERIFICATION_INCOMPLETE',
          statusCode: 503,
          userMessageKey: 'verification.timeout_saved_notice',
          cause: verifyError,
        })
      }
      const serverMessage = await readEdgeFnErrorBody(verifyError)
      const failure = verificationFailureClass(httpStatusOf(verifyError))
      throw new ServiceError({
        message: serverMessage ?? `License verification failed: ${verifyError.message}`,
        code: failure.code,
        statusCode: failure.statusCode,
        userMessageKey: verificationUserMessageKey(serverMessage),
        cause: verifyError,
      })
    }

    return {
      recordId: input.driverLicenseId,
      driverLicenseId: input.driverLicenseId,
      status: verifyData?.status ?? 'review',
      provider: verifyData?.provider ?? 'sertracen',
      extracted: verifyData?.extracted ?? {},
      confidence: verifyData?.confidence,
      rejectionReason: verifyData?.rejectionReason ?? null,
    }
  }

  /** Foreign documents use the existing provider pipeline, never Sertracen fields. */
  async confirmRegionalDriverLicense(
    profileId: string,
    input: {
      driverLicenseId: string
      licenseNumber?: string | null
      licenseClass: LicenseClass
      issueDate?: string | null
      expiryDate?: string | null
    },
  ): Promise<PhotoSubmitResult> {
    const liveId = await requireLiveUserId()
    if (liveId !== profileId) throw ServiceError.unauthorized('Session changed')
    await this.updateConfirmedDriverLicenseFields(liveId, input)
    const { data, error } = await verificationTransport.invoke<IdentityVerifyEdgeResponse>(
      'verify-license',
      { body: { driverLicenseId: input.driverLicenseId } },
      { recordId: input.driverLicenseId },
    )
    if (error) throw error
    return {
      recordId: input.driverLicenseId,
      driverLicenseId: input.driverLicenseId,
      status: data?.status ?? 'review',
      provider: data?.provider ?? 'manual',
      extracted: data?.extracted ?? {},
      rejectionReason: data?.rejectionReason ?? null,
    }
  }

  async confirmDriverLicenseForManualReview(
    profileId: string,
    input: {
      driverLicenseId: string
      licenseNumber?: string | null
      licenseClass: LicenseClass
      issueDate?: string | null
      expiryDate?: string | null
      extracted: PhotoSubmitResult['extracted']
      rejectionReason?: string | null
    },
  ): Promise<PhotoSubmitResult> {
    if (!profileId) {
      throw ServiceError.unauthorized('Authentication required for license verification')
    }

    await this.updateConfirmedDriverLicenseFields(profileId, input)

    const { error } = await supabase
      .from('driver_licenses')
      .update({
        status: 'submitted',
        canonical_status: 'review',
        provider_status: 'review',
        extracted_data: input.extracted as Json,
        rejection_reason: input.rejectionReason ?? null,
        verified_at: null,
      })
      .eq('id', input.driverLicenseId)
      .eq('profile_id', profileId)

    if (error) throw ServiceError.fromSupabaseError(error)

    return {
      recordId: input.driverLicenseId,
      driverLicenseId: input.driverLicenseId,
      status: 'review',
      provider: 'manual',
      extracted: input.extracted,
      rejectionReason: input.rejectionReason ?? null,
    }
  }

  private async updateConfirmedDriverLicenseFields(
    profileId: string,
    input: {
      driverLicenseId: string
      licenseNumber?: string | null
      licenseClass: LicenseClass
      issueDate?: string | null
      expiryDate?: string | null
    },
  ): Promise<void> {
    const patch: UpdateRow<'driver_licenses'> = {
      license_class: [input.licenseClass],
    }
    const licenseNumber = input.licenseNumber?.trim()
    if (licenseNumber) patch.license_number = licenseNumber
    const issueDate = validIsoDate(input.issueDate)
    if (issueDate) patch.issue_date = issueDate
    const expiryDate = validIsoDate(input.expiryDate)
    if (expiryDate) patch.expiry_date = expiryDate

    const { error } = await supabase
      .from('driver_licenses')
      .update(patch)
      .eq('id', input.driverLicenseId)
      .eq('profile_id', profileId)

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Submit a verification selfie.
   * Compresses image, uploads to storage, and updates identity_documents with selfie path.
   */
  async submitSelfie(userId: string, input: SelfieInput): Promise<void> {
    await this.submitAndVerifySelfie(userId, input)
  }

  /**
   * Photo-first identity submission.
   *
   * Compresses front + (optional) back photos, uploads them to Storage,
   * creates or updates the identity_documents row with minimum fields
   * (document_type, issuing_country, image paths), then invokes the
   * verify-identity edge function SYNCHRONOUSLY and returns the extracted
   * data + canonical status. The caller can then show the OCR'd fields
   * for inline confirmation/editing.
   *
   * This is the frictionless, photo-first path. Salvadoran DUI submissions
   * must complete through Verifik OCR plus the official Verifik DUI lookup.
   */
  async submitAndVerifyIdentity(
    userId: string,
    input: PhotoSubmitInput,
  ): Promise<PhotoSubmitResult> {
    // Storage RLS requires the object's top folder to equal auth.uid(), and the
    // identity_documents row is keyed on profile_id = auth.uid(). Resolve the
    // LIVE session identity so the path and the row can never be built from a
    // hook-cached user id that has drifted from the current session.
    const liveId = await requireLiveUserId()
    void userId

    const frontPath = `${liveId}/identity/${Crypto.randomUUID()}.jpg`
    const backPath = input.backImageUri ? `${liveId}/identity/${Crypto.randomUUID()}.jpg` : null

    const [frontStoragePath, backStoragePath] = await Promise.all([
      compressAndUpload(input.frontImageUri, BUCKET, frontPath),
      input.backImageUri && backPath
        ? compressAndUpload(input.backImageUri, BUCKET, backPath)
        : Promise.resolve(null),
    ])

    // document_number is NOT NULL in the schema — pass empty string as a
    // placeholder until a photo has actually been read.
    //
    // This comment used to promise that "the edge function will overwrite it
    // with the extracted value when OCR completes", and for a long time that
    // was simply untrue: verify-identity wrote extracted_data and left the
    // column at ''. Approved accounts existed whose canonical number was the
    // empty string, and this sentence is why nobody went looking. It is true
    // now — verify-identity writes document_number in the same UPDATE that
    // sets verified_at, and a BEFORE trigger normalises the shape
    // (20260809140000) — but the lesson is worth leaving here: a comment that
    // describes a guarantee is only as good as the test that holds it, and the
    // one holding this is scripts/validate-identity-number-canonical.mjs.
    //
    // The select-then-update path inside upsertActiveIdentityDocument means a
    // re-submission overwrites the existing in-flight row instead of inserting
    // a duplicate empty-numbered row that would (correctly) violate the
    // partial UNIQUE.
    const doc = await this.upsertActiveIdentityDocument(liveId, input.documentType, {
      // document_number is deliberately absent: the column is nullable and NULL
      // is the one value meaning "not read yet". It used to be written as '',
      // which every index predicate and every reader then had to know about,
      // and which let a row reach status='approved' still holding it.
      front_image_path: frontStoragePath,
      back_image_path: backStoragePath,
      // Reusing a row must not attach new evidence to an earlier confirmation.
      // The database enforces this too, including submissions from older apps.
      document_number: null,
      date_of_birth: null,
      first_name: null,
      last_name: null,
      extracted_data: null,
      verification_provider: null,
      provider_status: null,
      provider_reference: null,
      provider_response: null,
      confidence: null,
      issuing_country: input.issuingCountry.toUpperCase(),
      issuing_region: input.issuingRegion?.trim() || null,
      // EL CLIENTE NO ESCRIBE UN HECHO QUE LE PERTENECE AL SERVIDOR.
      //
      // Antes esto escribía `submitted`/`review` ANTES de llamar a
      // verify-identity. Si esa llamada fallaba —caída, rate limit, un 500— la
      // fila quedaba en "review" y el hub decía "queda en revisión", aunque
      // nadie iba a mirarla jamás: no hay sweeper ni cron para estas filas,
      // solo la cola manual del admin, que se alimenta de documentos que el
      // servidor realmente archivó. `pending` es la verdad entre la subida y la
      // respuesta: verify-identity es el ÚNICO que escribe `review` y la
      // decisión, así que un fallo en cualquier punto deja una fila que nadie
      // reclamó como en revisión.
      status: 'pending',
      canonical_status: 'pending',
      rejection_reason: null,
      verified_at: null,
      verified_by: null,
      selfie_image_path: null,
      liveness_score: null,
      face_match_score: null,
    })

    // Synchronous invoke — we want the extracted fields back for the confirm step.
    const { data: verifyData, error: verifyError } =
      await verificationTransport.invoke<IdentityVerifyEdgeResponse>(
        'verify-identity',
        {
          body: {
            identityDocumentId: doc.id,
            requestId: Crypto.randomUUID(),
            ...(input.extractOnly ? { extractOnly: true } : {}),
          },
        },
        { recordId: doc.id },
      )

    if (verifyError) return recoverPersistedIdentityFailure(liveId, doc.id, verifyError)

    return {
      recordId: doc.id,
      identityDocumentId: doc.id,
      nextStep: verifyData?.nextStep,
      status: verifyData?.status ?? 'review',
      provider: verifyData?.provider ?? 'manual',
      extracted: verifyData?.extracted ?? {},
      confidence: verifyData?.confidence,
      rejectionReason: verifyData?.rejectionReason ?? null,
    }
  }

  /**
   * Photo-first driver license submission. Mirrors submitAndVerifyIdentity
   * but writes to driver_licenses and invokes verify-license edge fn.
   */
  async submitAndVerifyLicense(
    userId: string,
    input: {
      issuingCountry: string
      frontImageUri: string
      backImageUri?: string
    },
  ): Promise<PhotoSubmitResult> {
    // Same invariant as submitAndVerifyIdentity: storage path folder and the
    // driver_licenses row (profile_id = auth.uid()) must come from the LIVE
    // session identity, not a cached user id that may have drifted.
    const liveId = await requireLiveUserId()
    void userId

    const frontPath = `${liveId}/license/${Crypto.randomUUID()}.jpg`
    const backPath = input.backImageUri ? `${liveId}/license/${Crypto.randomUUID()}.jpg` : null

    const [frontStoragePath, backStoragePath] = await Promise.all([
      compressAndUpload(input.frontImageUri, BUCKET, frontPath),
      input.backImageUri && backPath
        ? compressAndUpload(input.backImageUri, BUCKET, backPath)
        : Promise.resolve(null),
    ])

    // license_number is NOT NULL in the schema — placeholder overwritten by
    // the orchestrator once OCR extracts the real value. Same upsert-by-active
    // pattern as identity docs to avoid the ON CONFLICT specification error.
    const license = await this.upsertActiveDriverLicense(liveId, {
      license_number: '',
      license_class: ['liviana'], // default, overwritten by OCR if possible
      issue_date: new Date().toISOString().slice(0, 10),
      expiry_date: new Date(Date.now() + 365 * 24 * 3600_000).toISOString().slice(0, 10),
      front_image_path: frontStoragePath,
      back_image_path: backStoragePath,
      issuing_country: input.issuingCountry,
      status: 'submitted',
      canonical_status: 'review',
      rejection_reason: null,
      verified_at: null,
      verified_by: null,
    })

    const { data: verifyData, error: verifyError } =
      await verificationTransport.invoke<IdentityVerifyEdgeResponse>(
        'verify-license',
        { body: { driverLicenseId: license.id } },
        { recordId: license.id },
      )

    if (verifyError) {
      if (verifyError instanceof VerificationNetworkError) {
        throw new ServiceError({
          message: verifyError.message,
          code: 'VERIFICATION_INCOMPLETE',
          statusCode: 503,
          userMessageKey: 'verification.timeout_saved_notice',
          cause: verifyError,
        })
      }
      const serverMessage = await readEdgeFnErrorBody(verifyError)
      const failure = verificationFailureClass(httpStatusOf(verifyError))
      throw new ServiceError({
        message: serverMessage ?? `License verification failed: ${verifyError.message}`,
        code: failure.code,
        statusCode: failure.statusCode,
        userMessageKey: verificationUserMessageKey(serverMessage),
        cause: verifyError,
      })
    }

    return {
      recordId: license.id,
      driverLicenseId: license.id,
      status: verifyData?.status ?? 'review',
      provider: verifyData?.provider ?? 'manual',
      extracted: verifyData?.extracted ?? {},
      confidence: verifyData?.confidence,
      rejectionReason: verifyData?.rejectionReason ?? null,
    }
  }

  /**
   * Photo-first selfie submission with liveness + face match. Uploads the
   * selfie, invokes verify-selfie which runs Didit passive-liveness + face-
   * match against the stored identity document portrait, returns scores.
   */
  async submitAndVerifySelfie(
    userId: string,
    input: { selfieUri: string },
  ): Promise<{
    status: 'approved' | 'declined' | 'review'
    livenessScore: number | null
    faceMatchScore: number | null
    rejectionReason: string | null
  }> {
    // Selfie storage path and every identity_documents lookup/update below are
    // keyed on the LIVE session identity (auth.uid()), never a cached user id.
    const liveId = await requireLiveUserId()
    void userId

    const selfiePath = `${liveId}/selfie/${Crypto.randomUUID()}.jpg`
    const storagePath = await compressAndUpload(input.selfieUri, BUCKET, selfiePath)

    const { data: identityDoc, error: loadError } = await supabase
      .from('identity_documents')
      .select('id, status, provider_status')
      .eq('profile_id', liveId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (loadError) throw ServiceError.fromSupabaseError(loadError)

    // La selfie completa la cadena cuando el registro devolvió el DUI activo
    // pero sin biometría (ACTIVE_BUT_UNVERIFIED → provider_status
    // 'active_unverified'): en ese caso la selfie ES el paso final y su
    // aprobación aprueba el documento del lado del servidor.
    const docActiveUnverified =
      identityDoc?.status !== 'approved' && identityDoc?.provider_status === 'active_unverified'

    if (!identityDoc?.id || (identityDoc.status !== 'approved' && !docActiveUnverified)) {
      if (identityDoc?.id) {
        const { error: updateError } = await supabase
          .from('identity_documents')
          .update({ selfie_image_path: storagePath })
          .eq('id', identityDoc.id)
          .eq('profile_id', liveId)
        if (updateError) throw ServiceError.fromSupabaseError(updateError)
      }

      return {
        status: 'review',
        livenessScore: null,
        faceMatchScore: null,
        rejectionReason: 'Identidad pendiente de revisión.',
      }
    }

    const { data: verifyData, error: verifyError } =
      await verificationTransport.invoke<SelfieVerifyEdgeResponse>(
        'verify-selfie',
        {
          body: {
            profileId: liveId,
            identityDocumentId: identityDoc.id,
            selfieImagePath: storagePath,
          },
        },
        { recordId: identityDoc.id },
      )
    if (verifyError) {
      if (verifyError instanceof VerificationNetworkError) {
        throw new ServiceError({
          message: verifyError.message,
          code: 'VERIFICATION_INCOMPLETE',
          statusCode: 503,
          userMessageKey: 'verification.timeout_saved_notice',
          cause: verifyError,
        })
      }
      const serverMessage = await readEdgeFnErrorBody(verifyError)
      throw new ServiceError({
        message: serverMessage ?? `Selfie verification failed: ${verifyError.message}`,
        code: 'SELFIE_VERIFICATION_FAILED',
        statusCode: 500,
        userMessageKey: verificationUserMessageKey(serverMessage),
        cause: verifyError,
      })
    }

    return {
      status: verifyData?.status ?? 'review',
      livenessScore: verifyData?.livenessScore ?? null,
      faceMatchScore: verifyData?.faceMatchScore ?? null,
      rejectionReason: verifyData?.rejectionReason ?? null,
    }
  }

  /**
   * Start the provider-hosted live biometric session. The client opens the
   * provider URL, but approval is persisted only by Didit's signed webhook or
   * by the server-side refresh endpoint reading Didit's decision API.
   */
  async startLiveSelfieSession(
    userId: string,
    input: { callbackUrl?: string; native?: boolean },
  ): Promise<LiveSelfieSession> {
    if (!userId) {
      throw ServiceError.unauthorized('Authentication required for selfie verification')
    }

    const { data, error } = await verificationTransport.invoke('create-selfie-session', {
      body: { callbackUrl: input.callbackUrl, native: input.native === true },
    })

    if (error) {
      const { message: serverMessage, code: serverCode } = await readEdgeFnError(error)
      throw new ServiceError({
        message: serverMessage ?? error.message,
        code: 'SELFIE_SESSION_FAILED',
        statusCode: 500,
        userMessageKey: verificationUserMessageKey(serverMessage, serverCode),
        cause: error,
      })
    }

    const payload = asLiveSelfieSession(data)
    if (!payload) {
      throw new ServiceError({
        message: 'Invalid selfie session response',
        code: 'SELFIE_SESSION_INVALID',
        statusCode: 500,
        userMessageKey: 'verification.error_failed',
      })
    }
    return payload
  }

  async refreshLiveSelfieSession(
    userId: string,
    input: { sessionId?: string; providerSessionId?: string },
  ): Promise<LiveSelfieDecision> {
    if (!userId) {
      throw ServiceError.unauthorized('Authentication required for selfie verification')
    }

    const { data, error } = await verificationTransport.invoke('refresh-selfie-session', {
      body: input,
    })

    if (error) {
      const serverMessage = await readEdgeFnErrorBody(error)
      throw new ServiceError({
        message: serverMessage ?? error.message,
        code: 'SELFIE_SESSION_REFRESH_FAILED',
        statusCode: 500,
        userMessageKey: verificationUserMessageKey(serverMessage),
        cause: error,
      })
    }

    const payload = asLiveSelfieDecision(data)
    if (!payload) {
      throw new ServiceError({
        message: 'Invalid selfie decision response',
        code: 'SELFIE_DECISION_INVALID',
        statusCode: 500,
        userMessageKey: 'verification.error_failed',
      })
    }
    return payload
  }

  /**
   * Save user-confirmed OCR fields and ask the edge function to finish the
   * registry check. The edge owns the row lock, ownership check and provider
   * write; the client never performs a detached UPDATE before verification.
   */
  async confirmExtractedData(
    _userId: string,
    identityDocumentId: string,
    extracted: {
      firstName?: string | null
      lastName?: string | null
      dateOfBirth?: string | null
      documentNumber?: string | null
    },
  ): Promise<PhotoSubmitResult | null> {
    // `_userId` stays in the method signature for the older hook contract. The
    // edge authorizes from the live Supabase session and applies corrections
    // under the same row lock that claims the provider call. A client UPDATE
    // followed by invoke has a race where another device can replace these
    // fields in between.

    const { data: verifyData, error: verifyError } =
      await verificationTransport.invoke<IdentityVerifyEdgeResponse>(
        'verify-identity',
        {
          body: {
            identityDocumentId,
            requestId: Crypto.randomUUID(),
            confirmedFields: extracted,
          },
        },
        { recordId: identityDocumentId },
      )

    if (verifyError) {
      if (verifyError instanceof VerificationNetworkError) {
        const reconciled = await reconcileIdentityRow(_userId, identityDocumentId)
        if (reconciled) return reconciled
        throw new ServiceError({
          message: verifyError.message,
          code: 'VERIFICATION_INCOMPLETE',
          statusCode: 503,
          userMessageKey: 'verification.timeout_saved_notice',
          cause: verifyError,
        })
      }
      const { message: serverMessage, code } = await readEdgeFnError(verifyError)
      // El 4xx de este endpoint es una entrada rechazada ("Revisá los datos del
      // documento…"): esperado, no un defecto. Forzarlo a 500 lo mandaba a
      // Sentry como si el servidor se hubiera roto (TAGO-MOBILE-57).
      const failure = verificationFailureClass(httpStatusOf(verifyError))
      throw new ServiceError({
        message: serverMessage ?? `Identity verification failed: ${verifyError.message}`,
        code: failure.code,
        statusCode: failure.statusCode,
        userMessageKey: verificationUserMessageKey(serverMessage, code),
        cause: verifyError,
      })
    }

    return {
      recordId: identityDocumentId,
      identityDocumentId,
      nextStep: verifyData?.nextStep,
      status: verifyData?.status ?? 'review',
      provider: verifyData?.provider ?? 'manual',
      extracted: verifyData?.extracted ?? {},
      confidence: verifyData?.confidence,
      rejectionReason: verifyData?.rejectionReason ?? null,
    }
  }

  /**
   * Read the persisted row back after the client lost the provider response.
   * The server keeps running after the client aborts; this recovers the answer
   * without a second billable call.
   */
  async reconcileIdentityDocument(
    profileId: string,
    recordId: string,
  ): Promise<PhotoSubmitResult | null> {
    return reconcileIdentityRow(profileId, recordId)
  }

  /**
   * The submission that never reached a decision, for the identity screen to
   * hydrate on mount. A person whose OCR finished after their phone timed out
   * sees the fields here — with the stored photos reused, not recaptured.
   */
  async getPendingIdentitySubmission(profileId: string): Promise<PendingIdentitySubmission | null> {
    if (!profileId) return null

    const { data, error } = await supabase
      .from('identity_documents')
      .select(
        'id, status, canonical_status, provider_status, rejection_reason, extracted_data, ' +
          'document_number, first_name, last_name, date_of_birth, document_type, ' +
          'issuing_country, issuing_region, front_image_path, back_image_path',
      )
      .eq('profile_id', profileId)
      .or('status.in.(pending,submitted),canonical_status.in.(pending,in_progress,review)')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) throw ServiceError.fromSupabaseError(error)
    const row = data as Record<string, unknown> | null
    if (!row) return null

    const extracted = extractedFromRow(row)
    const frontPath = typeof row.front_image_path === 'string' ? row.front_image_path : ''
    const backPath = typeof row.back_image_path === 'string' ? row.back_image_path : ''
    const hasPhotos = frontPath.length > 0 || backPath.length > 0

    return {
      recordId: row.id as string,
      documentType: (row.document_type as IdDocumentTypeForPending) ?? 'dui',
      issuingCountry: (row.issuing_country as string) || 'SV',
      issuingRegion: (row.issuing_region as string | null) ?? null,
      canonicalStatus: asCanonicalStatus(row.canonical_status),
      providerStatus: (row.provider_status as string | null) ?? null,
      legacyStatus: extractStatus(row),
      extracted,
      hasPhotos,
      hasExtraction: hasReadableIdentityExtraction(extracted),
      rejectionReason: (row.rejection_reason as string) ?? null,
    }
  }

  /**
   * Re-run verification with the evidence ALREADY stored on the row — an
   * explicit user action after a lost response, never an automatic retry.
   * A fresh requestId is generated; the server's durable attempt either
   * replays the saved result (no re-bill) or, if still processing, answers
   * 409 IDENTITY_VERIFICATION_IN_PROGRESS which we surface honestly.
   */
  async resumeIdentitySubmission(
    profileId: string,
    recordId: string,
  ): Promise<PhotoSubmitResult | null> {
    if (!profileId || !recordId) return null

    const { data: verifyData, error: verifyError } =
      await verificationTransport.invoke<IdentityVerifyEdgeResponse>(
        'verify-identity',
        {
          body: {
            identityDocumentId: recordId,
            requestId: Crypto.randomUUID(),
          },
        },
        { recordId },
      )

    if (verifyError) return recoverPersistedIdentityFailure(profileId, recordId, verifyError)

    return {
      recordId,
      identityDocumentId: recordId,
      nextStep: verifyData?.nextStep,
      status: verifyData?.status ?? 'review',
      provider: verifyData?.provider ?? 'manual',
      extracted: verifyData?.extracted ?? {},
      confidence: verifyData?.confidence,
      rejectionReason: verifyData?.rejectionReason ?? null,
    }
  }

  /**
   * May this person be charged, and if not, exactly what is missing?
   *
   * Reads the same rental-aware predicate `create-payment` consults before it refuses. A
   * complete vehicle/date scope can unlock a scoped administrative deferral; a missing or
   * partial scope falls back to the normal profile-wide predicate and can never grant one.
   * This is still not a client re-derivation of the rule: this codebase already carries four
   * separate re-derivations of "is this person verified", and their disagreements are how a
   * screen ends up inviting a payment the server will decline.
   *
   * THROWS on failure rather than answering "not eligible". A network blip must not tell a
   * fully verified person they cannot pay — the caller keeps the checkout as it was and lets
   * the server have the final word, which it has anyway.
   */
  async getBookingEligibility(
    requiresAml?: boolean,
    scope?: BookingEligibilityScope,
  ): Promise<BookingEligibility> {
    const hasCompleteScope = Boolean(scope?.vehicleId && scope.startAt && scope.endAt)
    const { data, error } = hasCompleteScope
      ? await (supabase as unknown as ScopedBookingEligibilityRpcClient)
          .rpc('booking_eligibility_for_draft', {
            p_vehicle_id: scope!.vehicleId!,
            p_start_at: scope!.startAt!,
            p_end_at: scope!.endAt!,
            ...(requiresAml === undefined ? {} : { p_requires_aml: requiresAml }),
          })
          .maybeSingle()
      : await supabase
          .rpc(
            'booking_eligibility',
            requiresAml === undefined ? {} : { p_requires_aml: requiresAml },
          )
          .maybeSingle<{ allowed: boolean; missing: string[] | null }>()

    if (error) throw ServiceError.fromSupabaseError(error)

    return {
      allowed: data?.allowed === true,
      missing: (data?.missing ?? []).filter((reason): reason is BookingBlocker =>
        BOOKING_BLOCKERS.includes(reason as BookingBlocker),
      ),
      // A refusal we cannot explain is its own state. Without this, an unrecognised reason
      // would render as "nothing is missing" next to a blocked button — the exact dead end
      // this whole change exists to remove.
      hasUnknownBlocker:
        data?.allowed !== true &&
        (data?.missing ?? []).some(
          (reason) => !BOOKING_BLOCKERS.includes(reason as BookingBlocker),
        ),
    }
  }
}

// ─── Singleton Export ─────────────────────────────────────────────────────

export const verificationService = new VerificationServiceImpl()
